/**
 * Screen two. Above: every import and how much of it survived, or - op het
 * tweede tabblad - de zoekopdrachten die de extensie afloopt. Below: the same
 * houses the map shows, in a table, under the same filter.
 *
 * Dit is ook het scherm waar je de selectie samenstelt: welke imports meetellen
 * vink je in de tabel aan, de rest stel je in de balk in. Omdat het filter in de
 * querystring staat, neemt 'Op de kaart' die selectie ongewijzigd mee.
 *
 * De twee tabbladen leven in het hash-deel van de URL (`#zoekopdrachten`), los
 * van de querystring: die is van het filter en reist mee naar de kaart, en een
 * tabblad hoort daar niet in. Zo kan een mail wél rechtstreeks naar het tabblad
 * linken.
 */

import { useState } from 'react';
import { FilterBalk } from '@/components/filter/FilterBalk';
import { Melding } from '@/components/Melding';
import { useBereik, useDatasets, useFacetten } from '@/lib/api/queries';
import { useFilter } from '@/lib/filter-url';
import { DatasetTabel } from './DatasetTabel';
import { FotoStandRegel } from './FotoStand';
import { Importeren } from './Importeren';
import { WoningTabel } from './WoningTabel';
import { Zoekopdrachten } from './zoekopdrachten/Zoekopdrachten';

type Tabblad = 'datasets' | 'zoekopdrachten';

const HASH_ZOEKOPDRACHTEN = '#zoekopdrachten';

function tabbladUitHash(): Tabblad {
  if (typeof window === 'undefined') return 'datasets';
  return window.location.hash === HASH_ZOEKOPDRACHTEN ? 'zoekopdrachten' : 'datasets';
}

export function DatasetsScherm() {
  const state = useFilter();
  const facetten = useFacetten(state.filter);
  const bereik = useBereik(state.filter);
  const datasets = useDatasets();
  const [tabblad, zetTabblad] = useState<Tabblad>(tabbladUitHash);

  function kies(gekozen: Tabblad) {
    zetTabblad(gekozen);
    const hash = gekozen === 'zoekopdrachten' ? HASH_ZOEKOPDRACHTEN : '';
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}${hash}`,
    );
  }

  const tabbladen = (
    <div className="tabbladen" role="tablist" aria-label="Datasets of zoekopdrachten">
      <button
        type="button"
        role="tab"
        aria-selected={tabblad === 'datasets'}
        className="tabblad"
        onClick={() => kies('datasets')}
      >
        Datasets
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={tabblad === 'zoekopdrachten'}
        className="tabblad"
        onClick={() => kies('zoekopdrachten')}
      >
        Zoekopdrachten
      </button>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <FilterBalk state={state} facetten={facetten.data} bereik={bereik.data} naarKaart />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
          {tabblad === 'datasets' ? (
            <section className="flex flex-col gap-2">
              <Importeren kop={tabbladen} />

              {datasets.error ? <Melding fout={datasets.error} /> : null}
              {datasets.data ? <DatasetTabel datasets={datasets.data} state={state} /> : null}
              {datasets.data?.length === 0 ? (
                <p className="border border-line bg-surface-strong px-3 py-3 text-[12px] text-muted">
                  Nog niets ingeladen.
                </p>
              ) : null}
              <FotoStandRegel />
            </section>
          ) : (
            <section className="flex flex-col gap-2">
              <Zoekopdrachten kop={tabbladen} />
            </section>
          )}

          <section className="flex min-h-0 flex-col">
            <h2 className="kop mb-2 text-[15px]">Woningen</h2>
            <WoningTabel state={state} />
          </section>
        </div>
      </div>
    </div>
  );
}
